/* eslint-disable jsdoc/require-jsdoc -- 抽屉内部事件和映射由组件命名及页面测试约束。 */
import {
  Button,
  Drawer,
  Input,
  InputNumber,
  Select,
  Tag,
} from "@arco-design/web-react";
import { Upload, X } from "lucide-react";
import { useEffect, useRef, useState, type ChangeEvent } from "react";

import {
  stayHotelTypeOptions,
  stayCooperationStatusOptions,
  stayMediaSceneOptions,
  stayProcurementChannelOptions,
  stayRoomTypeOptions,
} from "../config";
import {
  toStayResourceSkuMutation,
  toStayResourceSpuMutation,
  type StayResourceMutationErrors,
} from "../service";
import type {
  StayGeoVerificationStatus,
  StayResourceMediaItem,
  StayResourceMediaMutation,
  StayResourceHousLevelOption,
  StayResourceSkuItem,
  StayResourceSkuMutation,
  StayResourceSpuItem,
  StayResourceSpuMutation,
  StayResourceSupplierItem,
  StayResourceSupplierMutation,
  StayResourceSupplierOption,
} from "../types";
import { getCityOptions, getDistrictOptions, getProvinceOptions } from "../../../shared/region";
import { StayResourceFormRow } from "./StayResourceFormRow";

type SaveResult = { ok: boolean; errors?: StayResourceMutationErrors };

export type SpuDrawerState = { type: "create" } | { type: "edit"; item: StayResourceSpuItem };

type SpuDrawerProps = {
  state: SpuDrawerState | null;
  pending: boolean;
  errorMessage: string;
  housLevelOptions: StayResourceHousLevelOption[];
  housLevelOptionsLoading: boolean;
  onClose: () => void;
  onCreateAndVerifyGeo: (payload: StayResourceSpuMutation) => Promise<SaveResult>;
  onUpdate: (spuId: string, payload: StayResourceSpuMutation) => Promise<SaveResult>;
};

const GEO_FORM_STATUS: Record<string, { label: string; color: string }> = {
  unverified: { label: "未核验", color: "gray" },
  pending: { label: "核验中", color: "blue" },
  resolved: { label: "已定位", color: "green" },
  review_required: { label: "待审核", color: "orange" },
  rejected: { label: "已驳回", color: "red" },
  failed: { label: "核验失败", color: "red" },
};

const emptySpu: StayResourceSpuMutation = {
  hotelName: "",
  hotelShortName: "",
  country: "中国",
  province: "",
  city: "",
  district: "",
  address: "",
  stayLevel: "",
  hotelType: "",
  remark: "",
};

/** 新增或编辑酒店主档，不提供普通经纬度输入。 */
export function StayResourceSpuDrawer({
  state,
  pending,
  errorMessage,
  housLevelOptions,
  housLevelOptionsLoading,
  onClose,
  onCreateAndVerifyGeo,
  onUpdate,
}: SpuDrawerProps) {
  const [payload, setPayload] = useState<StayResourceSpuMutation>(emptySpu);
  const [errors, setErrors] = useState<StayResourceMutationErrors>({});

  useEffect(() => {
    if (!state) return;
    setErrors({});
    setPayload(state.type === "create" ? { ...emptySpu } : toStayResourceSpuMutation(state.item));
  }, [state]);

  async function save() {
    if (!state || pending || state.type !== "edit") return;
    const result = await onUpdate(state.item.staySpuId, payload);
    if (!result.ok) return setErrors(result.errors ?? {});
    onClose();
  }

  async function saveAndVerifyGeo() {
    if (!state || pending || state.type !== "create") return;
    const result = await onCreateAndVerifyGeo(payload);
    if (!result.ok) return setErrors(result.errors ?? {});
    onClose();
  }

  const stayLevelOptions = withLegacyOption(
    housLevelOptions.map((item) => ({ value: item.levelName, label: item.levelName })),
    payload.stayLevel,
  );
  const hotelTypeOptions = withLegacyOption([...stayHotelTypeOptions], payload.hotelType);
  const isChina = payload.country.trim() === "中国";
  const provinceOptions = preserveCurrent(getProvinceOptions(), payload.province);
  const cityOptions = preserveCurrent(getCityOptions(payload.province), payload.city);
  const districtOptions = preserveCurrent(
    getDistrictOptions(payload.province, payload.city),
    payload.district,
  );
  const footer = state?.type === "create" ? (
    <div className="stay-resource-drawer-footer-actions">
      <Button type="primary" loading={pending} onClick={saveAndVerifyGeo}>保存并执行 GEO</Button>
    </div>
  ) : (
    <div className="stay-resource-drawer-footer-actions">
      <Button type="primary" loading={pending} onClick={save}>保存</Button>
    </div>
  );

  return (
    <Drawer
      {...dialogProps(state?.type === "edit" ? "编辑酒店" : "新增酒店")}
      className="stay-resource-form-drawer"
      title={state?.type === "edit" ? "编辑酒店" : "新增酒店"}
      visible={Boolean(state)}
      width="42%"
      unmountOnExit
      closeIcon={<Button type="text" size="mini" aria-label="关闭" icon={<X size={16} />} />}
      footer={footer}
      onCancel={() => !pending && onClose()}
    >
      <div className="stay-resource-form">
        {errorMessage ? <div className="stay-resource-error" role="alert">{errorMessage}</div> : null}
        <StayResourceFormRow label="酒店名称" required error={errors.hotelName}>
          <Input aria-label="酒店名称" value={payload.hotelName} onChange={(hotelName) => setPayload({ ...payload, hotelName })} />
        </StayResourceFormRow>
        <StayResourceFormRow label="酒店简称" error={errors.hotelShortName}>
          <Input aria-label="酒店简称" value={payload.hotelShortName} onChange={(hotelShortName) => setPayload({ ...payload, hotelShortName })} />
        </StayResourceFormRow>
        <StayResourceFormRow label="国家或地区" required error={errors.country}>
          <Input
            aria-label="国家或地区"
            value={payload.country}
            onChange={(country) => setPayload({ ...payload, country, province: "", city: "", district: "" })}
          />
        </StayResourceFormRow>
        {isChina ? (
          <>
            <StayResourceFormRow label="省份" required error={errors.province || errors.region}>
              <Select
                allowClear
                showSearch
                aria-label="省份"
                data-testid="stay-resource-province"
                options={provinceOptions.map((value) => ({ label: value, value }))}
                value={payload.province || undefined}
                onChange={(province) => setPayload({ ...payload, province: province || "", city: "", district: "" })}
              />
            </StayResourceFormRow>
            <StayResourceFormRow label="城市" required error={errors.city}>
              <Select
                allowClear
                showSearch
                aria-label="城市"
                data-testid="stay-resource-city"
                disabled={!payload.province}
                options={cityOptions.map((value) => ({ label: value, value }))}
                value={payload.city || undefined}
                onChange={(city) => setPayload({ ...payload, city: city || "", district: "" })}
              />
            </StayResourceFormRow>
            <StayResourceFormRow label="区县" error={errors.district}>
              <Select
                allowClear
                showSearch
                aria-label="区县"
                data-testid="stay-resource-district"
                disabled={!payload.city}
                options={districtOptions.map((value) => ({ label: value, value }))}
                value={payload.district || undefined}
                onChange={(district) => setPayload({ ...payload, district: district || "" })}
              />
            </StayResourceFormRow>
          </>
        ) : (
          <>
            <StayResourceFormRow label="省份" required error={errors.province}>
              <Input
                aria-label="省份"
                value={payload.province}
                onChange={(province) => setPayload({ ...payload, province, city: "", district: "" })}
              />
            </StayResourceFormRow>
            <StayResourceFormRow label="城市" required error={errors.city}>
              <Input
                aria-label="城市"
                value={payload.city}
                onChange={(city) => setPayload({ ...payload, city, district: "" })}
              />
            </StayResourceFormRow>
            <StayResourceFormRow label="区县" error={errors.district}>
              <Input aria-label="区县" value={payload.district} onChange={(district) => setPayload({ ...payload, district })} />
            </StayResourceFormRow>
          </>
        )}
        <StayResourceFormRow label="详细地址" error={errors.address}>
          <Input aria-label="详细地址" value={payload.address} onChange={(address) => setPayload({ ...payload, address })} />
        </StayResourceFormRow>
        <StayResourceFormRow label="住宿档次" error={errors.stayLevel}>
          <Select
            aria-label="住宿档次"
            allowClear
            loading={housLevelOptionsLoading}
            options={stayLevelOptions}
            value={payload.stayLevel || undefined}
            onChange={(stayLevel) => setPayload({ ...payload, stayLevel: stayLevel || "" })}
          />
        </StayResourceFormRow>
        <StayResourceFormRow label="酒店类型" error={errors.hotelType}>
          <Select
            aria-label="酒店类型"
            allowClear
            options={hotelTypeOptions}
            value={payload.hotelType || undefined}
            onChange={(hotelType) => setPayload({ ...payload, hotelType: hotelType || "" })}
          />
        </StayResourceFormRow>
        <StayResourceFormRow label="公开寻源电话">
          <Input.TextArea aria-label="公开寻源电话" placeholder="多个电话用换行分隔" value={(payload.publicPhones ?? []).join("\n")} onChange={(value) => setPayload({ ...payload, publicPhones: value.split(/[\n,，;；]+/).map((v) => v.trim()).filter(Boolean) })} />
        </StayResourceFormRow>
        <StayResourceFormRow label="房间总数">
          <InputNumber aria-label="房间总数" min={0} max={100000} precision={0} value={payload.roomCount} onChange={(roomCount) => setPayload({ ...payload, roomCount })} />
        </StayResourceFormRow>
        <StayResourceFormRow label="开业年份">
          <InputNumber aria-label="开业年份" min={1800} max={2100} precision={0} value={payload.openingYear} onChange={(openingYear) => setPayload({ ...payload, openingYear })} />
        </StayResourceFormRow>
        <StayResourceFormRow label="装修年份">
          <InputNumber aria-label="装修年份" min={1800} max={2100} precision={0} value={payload.renovationYear} onChange={(renovationYear) => setPayload({ ...payload, renovationYear })} />
        </StayResourceFormRow>
        <StayResourceFormRow label="平台评分">
          <InputNumber aria-label="平台评分" min={0} max={5} precision={2} value={payload.platformRating} onChange={(platformRating) => setPayload({ ...payload, platformRating })} />
        </StayResourceFormRow>
        <StayResourceFormRow label="GEO">
          <StayResourceSpuGeoSummary item={state?.type === "edit" ? state.item : undefined} />
        </StayResourceFormRow>
        <StayResourceFormRow label="备注" error={errors.remark}>
          <Input.TextArea aria-label="备注" autoSize={{ minRows: 3, maxRows: 7 }} value={payload.remark} onChange={(remark) => setPayload({ ...payload, remark })} />
        </StayResourceFormRow>
      </div>
    </Drawer>
  );
}

function StayResourceSpuGeoSummary({ item }: { item?: StayResourceSpuItem }) {
  if (!item) {
    return (
      <div className="stay-resource-geo-hint">
        GEO 由后端使用酒店名称、省份、城市、区县和详细地址核验后写入；低置信或多候选会进入人工审核，不在表单中直接编辑经纬度。
      </div>
    );
  }

  const status = (item.geoVerificationStatus ?? item.geoStatus ?? "unverified") as StayGeoVerificationStatus | string;
  const current = GEO_FORM_STATUS[status] ?? { label: String(status), color: "gray" };
  const coords = item.lng !== undefined && item.lat !== undefined
    ? `${item.lng.toFixed(6)}, ${item.lat.toFixed(6)}`
    : undefined;
  const showResult = status !== "unverified";

  if (!showResult) {
    return (
      <div className="stay-resource-geo-hint">
        尚未执行 GEO。保存地址后，请到详情「地图与 GEO」发起核验；不在表单中直接编辑经纬度。
      </div>
    );
  }

  return (
    <div className="stay-resource-geo-summary" aria-label="GEO 状态摘要">
      <div className="stay-resource-geo-summary__status">
        <Tag color={current.color}>{current.label}</Tag>
        {item.geoCoordinateSystem ? <span>{item.geoCoordinateSystem}</span> : null}
      </div>
      {coords ? (
        <div className="stay-resource-geo-summary__row">
          <span>最终坐标</span>
          <strong>{coords}</strong>
        </div>
      ) : null}
      {item.geoAmapPoiId ? (
        <div className="stay-resource-geo-summary__row">
          <span>POI</span>
          <strong>{item.geoAmapPoiId}</strong>
        </div>
      ) : null}
      {item.geoLastVerifiedAt ? (
        <div className="stay-resource-geo-summary__row">
          <span>最近核验</span>
          <strong>{item.geoLastVerifiedAt.replace("T", " ").slice(0, 19)}</strong>
        </div>
      ) : null}
      {item.geoReviewedAt ? (
        <div className="stay-resource-geo-summary__row">
          <span>最近审核</span>
          <strong>{item.geoReviewedAt.replace("T", " ").slice(0, 19)}</strong>
        </div>
      ) : null}
      {item.geoFailureReason ? (
        <div className="stay-resource-geo-summary__row stay-resource-geo-summary__row--muted">
          <span>原因</span>
          <strong>{item.geoFailureReason}</strong>
        </div>
      ) : null}
      <div className="stay-resource-geo-summary__hint">
        {status === "review_required"
          ? "请到详情「地图与 GEO」完成人工通过或驳回；不在表单中直接编辑经纬度。"
          : status === "resolved"
            ? "坐标已写入主档；重新核验请到详情「地图与 GEO」。"
            : "重新核验请到详情「地图与 GEO」；不在表单中直接编辑经纬度。"}
      </div>
    </div>
  );
}

export type SkuDrawerState =
  | { type: "create"; spu: StayResourceSpuItem }
  | { type: "edit"; spu: StayResourceSpuItem; item: StayResourceSkuItem };

type SkuDrawerProps = {
  state: SkuDrawerState | null;
  pending: boolean;
  errorMessage: string;
  onClose: () => void;
  onCreate: (spuId: string, payload: StayResourceSkuMutation) => Promise<SaveResult>;
  onUpdate: (spuId: string, skuId: string, payload: StayResourceSkuMutation) => Promise<SaveResult>;
};

const emptySku: StayResourceSkuMutation = {
  skuName: "",
  roomType: "",
  roomUsageLabel: "",
  roomCapacity: undefined,
  remark: "",
};

/** 新增或编辑酒店房型，表单不含价格字段。 */
export function StayResourceSkuDrawer(props: SkuDrawerProps) {
  const [payload, setPayload] = useState<StayResourceSkuMutation>(emptySku);
  const [errors, setErrors] = useState<StayResourceMutationErrors>({});

  useEffect(() => {
    if (!props.state) return;
    setErrors({});
    setPayload(props.state.type === "create"
      ? { ...emptySku }
      : toStayResourceSkuMutation(props.state.item));
  }, [props.state]);

  async function save() {
    if (!props.state || props.pending) return;
    const result = props.state.type === "create"
      ? await props.onCreate(props.state.spu.staySpuId, payload)
      : await props.onUpdate(props.state.spu.staySpuId, props.state.item.staySkuId, payload);
    if (!result.ok) return setErrors(result.errors ?? {});
    props.onClose();
  }

  return (
    <Drawer
      {...dialogProps(props.state?.type === "edit" ? "编辑房型" : "新增房型")}
      className="stay-resource-form-drawer"
      title={props.state?.type === "edit" ? "编辑房型" : "新增房型"}
      visible={Boolean(props.state)}
      width="42%"
      unmountOnExit
      closeIcon={<Button type="text" size="mini" aria-label="关闭" icon={<X size={16} />} />}
      footer={<Button type="primary" long loading={props.pending} onClick={save}>保存</Button>}
      onCancel={() => !props.pending && props.onClose()}
    >
      <div className="stay-resource-form">
        {props.state ? <div className="stay-resource-form__parent">所属酒店：{props.state.spu.hotelName}</div> : null}
        {props.errorMessage ? <div className="stay-resource-error" role="alert">{props.errorMessage}</div> : null}
        <StayResourceFormRow label="房型名称" required error={errors.skuName}>
          <Input aria-label="房型名称" value={payload.skuName} onChange={(skuName) => setPayload({ ...payload, skuName })} />
        </StayResourceFormRow>
        <StayResourceFormRow label="物理房型" required error={errors.roomType}>
          <Select
            aria-label="物理房型"
            options={withLegacyOption([...stayRoomTypeOptions], payload.roomType)}
            value={payload.roomType || undefined}
            onChange={(roomType) => setPayload({ ...payload, roomType: roomType || "" })}
          />
        </StayResourceFormRow>
        <StayResourceFormRow label="用途标签" error={errors.roomUsageLabel}>
          <Input aria-label="用途标签" value={payload.roomUsageLabel} onChange={(roomUsageLabel) => setPayload({ ...payload, roomUsageLabel })} />
        </StayResourceFormRow>
        <StayResourceFormRow label="入住人数" error={errors.roomCapacity}>
          <InputNumber aria-label="入住人数" min={1} precision={0} value={payload.roomCapacity} onChange={(roomCapacity) => setPayload({ ...payload, roomCapacity: finiteNumber(roomCapacity) })} />
        </StayResourceFormRow>
        <StayResourceFormRow label="备注" error={errors.remark}>
          <Input.TextArea aria-label="备注" autoSize={{ minRows: 3, maxRows: 7 }} value={payload.remark} onChange={(remark) => setPayload({ ...payload, remark })} />
        </StayResourceFormRow>
      </div>
    </Drawer>
  );
}

export type MediaDrawerState =
  | { type: "create"; spu: StayResourceSpuItem }
  | { type: "edit"; spu: StayResourceSpuItem; item: StayResourceMediaItem };

type MediaDrawerProps = {
  state: MediaDrawerState | null;
  skus: StayResourceSkuItem[];
  pending: boolean;
  errorMessage: string;
  onClose: () => void;
  onUpload: (file: File) => Promise<string | undefined>;
  onCreate: (spuId: string, payload: StayResourceMediaMutation) => Promise<SaveResult>;
  onUpdate: (spuId: string, mediaId: string, payload: StayResourceMediaMutation) => Promise<SaveResult>;
};

const emptyMedia: StayResourceMediaMutation = {
  staySkuId: undefined,
  mediaScene: "cover",
  mediaName: "",
  fileUrl: "",
  fileExt: "",
  contentText: "",
  sortNo: 0,
  remark: "",
};

/** 上传文件并维护酒店级或显式 SKU 级展示元数据。 */
export function StayResourceMediaDrawer(props: MediaDrawerProps) {
  const [payload, setPayload] = useState<StayResourceMediaMutation>(emptyMedia);
  const [errors, setErrors] = useState<StayResourceMutationErrors>({});
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!props.state) return;
    setErrors({});
    setPayload(props.state.type === "create" ? { ...emptyMedia } : mediaToMutation(props.state.item));
  }, [props.state]);

  async function upload(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fileUrl = await props.onUpload(file);
      if (fileUrl) {
        setPayload((current) => ({
          ...current,
          fileUrl,
          mediaName: current.mediaName || file.name,
          fileExt: file.name.includes(".") ? file.name.split(".").pop() : undefined,
        }));
      }
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  }

  async function save() {
    if (!props.state || props.pending || uploading) return;
    const result = props.state.type === "create"
      ? await props.onCreate(props.state.spu.staySpuId, payload)
      : await props.onUpdate(props.state.spu.staySpuId, props.state.item.stayMediaId, payload);
    if (!result.ok) return setErrors(result.errors ?? {});
    props.onClose();
  }

  return (
    <Drawer
      {...dialogProps(props.state?.type === "edit" ? "编辑媒体" : "新增媒体")}
      className="stay-resource-form-drawer"
      title={props.state?.type === "edit" ? "编辑媒体" : "新增媒体"}
      visible={Boolean(props.state)}
      width="42%"
      unmountOnExit
      closeIcon={<Button type="text" size="mini" aria-label="关闭" icon={<X size={16} />} />}
      footer={<Button type="primary" long loading={props.pending || uploading} onClick={save}>保存元数据</Button>}
      onCancel={() => !props.pending && !uploading && props.onClose()}
    >
      <div className="stay-resource-form">
        {props.errorMessage ? <div className="stay-resource-error" role="alert">{props.errorMessage}</div> : null}
        <StayResourceFormRow label="上传文件" required error={errors.fileUrl}>
          <label className="stay-resource-file-button">
            <Upload size={16} />{uploading ? "上传中..." : "选择并上传"}
            <input aria-label="上传文件" type="file" accept="image/*,video/*" disabled={uploading || props.pending} onChange={upload} />
          </label>
        </StayResourceFormRow>
        {payload.fileUrl ? <div className="stay-resource-file-url">{payload.fileUrl}</div> : null}
        <StayResourceFormRow label="媒体名称" required error={errors.mediaName}>
          <Input aria-label="媒体名称" value={payload.mediaName} onChange={(mediaName) => setPayload({ ...payload, mediaName })} />
        </StayResourceFormRow>
        <StayResourceFormRow label="展示场景" required error={errors.mediaScene}>
          <Select aria-label="展示场景" options={[...stayMediaSceneOptions]} value={payload.mediaScene} onChange={(mediaScene) => setPayload({ ...payload, mediaScene, staySkuId: mediaScene === "room" ? payload.staySkuId : undefined })} />
        </StayResourceFormRow>
        <StayResourceFormRow label="关联房型" required={payload.mediaScene === "room"} error={errors.staySkuId}>
          <Select
            aria-label="关联房型"
            allowClear={payload.mediaScene !== "room"}
            disabled={payload.mediaScene !== "room"}
            placeholder={payload.mediaScene === "room" ? (props.skus.length ? "请选择对应房型" : "请先新增房型") : "当前场景为酒店级，无需选房型"}
            options={props.skus.map((sku) => ({ label: sku.skuName, value: sku.staySkuId }))}
            value={payload.staySkuId}
            onChange={(staySkuId) => setPayload({ ...payload, staySkuId: staySkuId || undefined })}
          />
        </StayResourceFormRow>
        <StayResourceFormRow label="说明文本" error={errors.contentText}>
          <Input.TextArea aria-label="说明文本" autoSize={{ minRows: 2, maxRows: 5 }} value={payload.contentText} onChange={(contentText) => setPayload({ ...payload, contentText })} />
        </StayResourceFormRow>
        <StayResourceFormRow label="排序号" error={errors.sortNo}>
          <InputNumber aria-label="排序号" min={0} precision={0} value={payload.sortNo} onChange={(sortNo) => setPayload({ ...payload, sortNo: finiteNumber(sortNo) ?? 0 })} />
        </StayResourceFormRow>
        <StayResourceFormRow label="备注" error={errors.remark}>
          <Input.TextArea aria-label="媒体备注" autoSize={{ minRows: 2, maxRows: 5 }} value={payload.remark} onChange={(remark) => setPayload({ ...payload, remark })} />
        </StayResourceFormRow>
      </div>
    </Drawer>
  );
}

export type SupplierDrawerState =
  | { type: "create"; spu: StayResourceSpuItem }
  | { type: "edit"; spu: StayResourceSpuItem; item: StayResourceSupplierItem };

type SupplierDrawerProps = {
  state: SupplierDrawerState | null;
  skus: StayResourceSkuItem[];
  pending: boolean;
  errorMessage: string;
  onClose: () => void;
  onSearchSuppliers: (query: string) => Promise<StayResourceSupplierOption[]>;
  onCreate: (spuId: string, payload: StayResourceSupplierMutation) => Promise<SaveResult>;
  onUpdate: (spuId: string, capabilityId: string, payload: StayResourceSupplierMutation) => Promise<SaveResult>;
};

const emptySupplier: StayResourceSupplierMutation = {
  staySkuId: undefined,
  supplierId: "",
  procurementChannelType: "direct_resource_provider",
  cooperationStatus: "candidate",
  validFrom: "",
  validTo: "",
  evidenceType: "",
  evidenceId: "",
  evidenceSummary: "",
  reviewStatus: "pending",
  remark: "",
};

/** 新增或编辑统一住宿供应能力。 */
export function StayResourceSupplierDrawer(props: SupplierDrawerProps) {
  const [payload, setPayload] = useState<StayResourceSupplierMutation>(emptySupplier);
  const [errors, setErrors] = useState<StayResourceMutationErrors>({});
  const [supplierOptions, setSupplierOptions] = useState<StayResourceSupplierOption[]>([]);
  const [supplierOptionsLoading, setSupplierOptionsLoading] = useState(false);
  const [supplierOptionsError, setSupplierOptionsError] = useState("");
  const supplierOptionsRequest = useRef(0);

  useEffect(() => {
    if (!props.state) return;
    setErrors({});
    setPayload(props.state.type === "create" ? { ...emptySupplier } : supplierToMutation(props.state.item));
    setSupplierOptionsError("");
    if (props.state.type === "edit") {
      supplierOptionsRequest.current += 1;
      setSupplierOptions([{
        supplierId: props.state.item.supplierId,
        supplierCode: props.state.item.supplierId,
        supplierName: props.state.item.supplierName,
        supplierType: "",
        canBeFulfillmentSubject: true,
      }]);
      setSupplierOptionsLoading(false);
      return;
    }
    void loadSupplierOptions("");
  }, [props.state]);

  async function loadSupplierOptions(query: string) {
    const requestId = ++supplierOptionsRequest.current;
    setSupplierOptionsLoading(true);
    setSupplierOptionsError("");
    try {
      const options = await props.onSearchSuppliers(query);
      if (requestId === supplierOptionsRequest.current) setSupplierOptions(options);
    } catch {
      if (requestId === supplierOptionsRequest.current) {
        setSupplierOptions([]);
        setSupplierOptionsError("供应商候选加载失败，请稍后重试");
      }
    } finally {
      if (requestId === supplierOptionsRequest.current) setSupplierOptionsLoading(false);
    }
  }

  async function save() {
    if (!props.state || props.pending) return;
    const result = props.state.type === "create"
      ? await props.onCreate(props.state.spu.staySpuId, payload)
      : await props.onUpdate(props.state.spu.staySpuId, props.state.item.capabilityId, payload);
    if (!result.ok) return setErrors(result.errors ?? {});
    props.onClose();
  }

  return (
    <Drawer
      {...dialogProps(props.state?.type === "edit" ? "编辑供应能力" : "新增供应能力")}
      className="stay-resource-form-drawer"
      title={props.state?.type === "edit" ? "编辑供应能力" : "新增供应能力"}
      visible={Boolean(props.state)}
      width="42%"
      unmountOnExit
      closeIcon={<Button type="text" size="mini" aria-label="关闭" icon={<X size={16} />} />}
      footer={<Button type="primary" long loading={props.pending} onClick={save}>保存</Button>}
      onCancel={() => !props.pending && props.onClose()}
    >
      <div className="stay-resource-form">
        {props.errorMessage ? <div className="stay-resource-error" role="alert">{props.errorMessage}</div> : null}
        <StayResourceFormRow label="供应商" required error={errors.supplierId ?? supplierOptionsError}>
          <Select
            aria-label="供应商"
            showSearch
            allowClear={props.state?.type !== "edit"}
            filterOption={false}
            loading={supplierOptionsLoading}
            disabled={props.state?.type === "edit"}
            placeholder="按名称或编号搜索可履约供应商"
            notFoundContent={supplierOptionsLoading ? "正在加载供应商" : "暂无可履约供应商"}
            options={supplierOptions.map((option) => ({
              value: option.supplierId,
              label: supplierOptionLabel(option),
            }))}
            value={payload.supplierId || undefined}
            onSearch={(query) => void loadSupplierOptions(query)}
            onChange={(supplierId) => setPayload({ ...payload, supplierId: supplierId || "" })}
          />
        </StayResourceFormRow>
        <StayResourceFormRow label="能力范围">
          <Select aria-label="能力范围" allowClear placeholder="酒店级" options={props.skus.map((sku) => ({ label: sku.skuName, value: sku.staySkuId }))} value={payload.staySkuId} onChange={(staySkuId) => setPayload({ ...payload, staySkuId: staySkuId || undefined })} />
        </StayResourceFormRow>
        <StayResourceFormRow label="采购渠道" required error={errors.procurementChannelType}>
          <Select aria-label="采购渠道" options={[...stayProcurementChannelOptions]} value={payload.procurementChannelType} onChange={(procurementChannelType) => setPayload({ ...payload, procurementChannelType })} />
        </StayResourceFormRow>
        <StayResourceFormRow label="合作状态" required error={errors.cooperationStatus}>
          <Select aria-label="合作状态" options={[...stayCooperationStatusOptions]} value={payload.cooperationStatus} onChange={(cooperationStatus) => setPayload({ ...payload, cooperationStatus })} />
        </StayResourceFormRow>
        <StayResourceFormRow label="生效日期">
          <input className="stay-resource-native-input" aria-label="生效日期" type="date" value={payload.validFrom} onChange={(event) => setPayload({ ...payload, validFrom: event.target.value })} />
        </StayResourceFormRow>
        <StayResourceFormRow label="失效日期" error={errors.validTo}>
          <input className="stay-resource-native-input" aria-label="失效日期" type="date" value={payload.validTo} onChange={(event) => setPayload({ ...payload, validTo: event.target.value })} />
        </StayResourceFormRow>
        <StayResourceFormRow label="证据类型" error={errors.evidenceType}>
          <Input aria-label="证据类型" value={payload.evidenceType} onChange={(evidenceType) => setPayload({ ...payload, evidenceType })} />
        </StayResourceFormRow>
        <StayResourceFormRow label="证据 ID" error={errors.evidenceId}>
          <Input aria-label="证据 ID" value={payload.evidenceId} onChange={(evidenceId) => setPayload({ ...payload, evidenceId })} />
        </StayResourceFormRow>
        <StayResourceFormRow label="证据摘要" error={errors.evidenceSummary}>
          <Input.TextArea aria-label="证据摘要" autoSize={{ minRows: 3, maxRows: 6 }} value={payload.evidenceSummary} onChange={(evidenceSummary) => setPayload({ ...payload, evidenceSummary })} />
        </StayResourceFormRow>
        <StayResourceFormRow label="备注" error={errors.remark}>
          <Input.TextArea aria-label="供应能力备注" autoSize={{ minRows: 2, maxRows: 5 }} value={payload.remark} onChange={(remark) => setPayload({ ...payload, remark })} />
        </StayResourceFormRow>
      </div>
    </Drawer>
  );
}

function mediaToMutation(item: StayResourceMediaItem): StayResourceMediaMutation {
  return {
    staySkuId: item.staySkuId,
    mediaScene: item.mediaScene,
    mediaName: item.mediaName,
    fileUrl: item.fileUrl,
    fileExt: item.fileExt ?? "",
    contentText: item.contentText ?? "",
    sortNo: item.sortNo,
    remark: item.remark,
  };
}

function supplierToMutation(item: StayResourceSupplierItem): StayResourceSupplierMutation {
  return {
    staySkuId: item.staySkuId,
    supplierId: item.supplierId,
    procurementChannelType: item.procurementChannelType,
    cooperationStatus: item.cooperationStatus,
    validFrom: item.validFrom ?? "",
    validTo: item.validTo ?? "",
    evidenceType: item.evidenceType ?? "",
    evidenceId: item.evidenceId ?? "",
    evidenceSummary: item.evidenceSummary ?? "",
    reviewStatus: item.reviewStatus === "approved" || item.reviewStatus === "rejected"
      ? item.reviewStatus
      : "pending",
    remark: item.remark,
  };
}

function supplierOptionLabel(option: StayResourceSupplierOption): string {
  return option.supplierType
    ? `${option.supplierName} · ${option.supplierType}`
    : option.supplierName;
}

function dialogProps(label: string) {
  return { role: "dialog", "aria-label": label, "aria-modal": "true" } as Record<string, string>;
}

function withLegacyOption<const T extends { value: string; label: string }>(
  options: T[],
  value?: string,
): Array<T | { value: string; label: string }> {
  const normalized = value?.trim();
  if (!normalized || options.some((option) => option.value === normalized)) {
    return options;
  }
  return [{ value: normalized, label: `${normalized}（历史值）` }, ...options];
}

/** 保留当前非标准行政区值，避免编辑历史脏数据时选项丢失。 */
function preserveCurrent(options: string[], current?: string) {
  const normalized = current?.trim();
  if (!normalized || options.includes(normalized)) {
    return options;
  }
  return [normalized, ...options];
}

function finiteNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}
